import React, { useState } from 'react'
import { fetchJSON } from '@/pages/admin/_admin-utils'

type Props = {
  ticketId: string
  walletContext: 'teacher' | 'locker_room' | 'sponsor' | string | null
  bearer: Record<string, string> | null
}

export function TicketLedgerAdjustmentForm({ ticketId, walletContext, bearer }: Props) {
  const [deltaCredits, setDeltaCredits] = useState('')
  const [deltaCents, setDeltaCents] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  if (!walletContext || walletContext === 'none') {
    return <div className="text-xs text-slate-500">This ticket has no wallet context; no adjustment form.</div>
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    setSuccess(null)

    try {
      const body = {
        ticket_id: ticketId,
        delta_credits: deltaCredits ? Number(deltaCredits) : 0,
        delta_cents: deltaCents ? Number(deltaCents) : 0,
        note,
      }

      const result = await fetchJSON(`/api/v1/admin/tickets/${ticketId}/ledger-adjust`, {
        method: 'POST',
        bearer,
        body,
      }) as { ledger_adjustment_id?: string } | null

      if (!result?.ledger_adjustment_id) {
        throw new Error('Adjustment response missing ledger entry id')
      }

      setSuccess('Ledger adjustment recorded and linked to this ticket.')
      setDeltaCredits('')
      setDeltaCents('')
      setNote('')
    } catch (err: any) {
      setError(err?.message || 'Adjustment failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="mt-3 rounded-md border p-3 text-sm">
      <div className="font-medium mb-2">Record ledger adjustment ({walletContext})</div>

      <div className="mb-2 flex gap-3">
        <div>
          <label className="mb-1 block text-xs font-medium">Credits (Δ)</label>
          <input
            type="number"
            className="w-24 rounded border px-2 py-1 text-sm"
            value={deltaCredits}
            onChange={(e) => setDeltaCredits(e.target.value)}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">Cents (Δ)</label>
          <input
            type="number"
            className="w-24 rounded border px-2 py-1 text-sm"
            value={deltaCents}
            onChange={(e) => setDeltaCents(e.target.value)}
          />
        </div>
      </div>

      <div className="mb-2">
        <label className="mb-1 block text-xs font-medium">Note</label>
        <textarea
          className="w-full rounded border px-2 py-1 text-sm"
          rows={2}
          placeholder="Adjustment reason for audit trail…"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>

      {error ? <div className="mb-1 text-xs text-red-600">{error}</div> : null}
      {success ? <div className="mb-1 text-xs text-emerald-600">{success}</div> : null}

      <button
        type="submit"
        className="inline-flex items-center rounded border bg-slate-900 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
        disabled={busy}
      >
        {busy ? 'Recording…' : 'Record adjustment'}
      </button>

      <div className="mt-1 text-[11px] text-slate-500">
        This will create a ledger entry, update the wallet balance, and link the adjustment to this ticket.
      </div>
    </form>
  )
}

export default TicketLedgerAdjustmentForm
